import {
  deleteStoredBlog,
  getAllBlogs,
  getBlogBySlug,
  getPublishedBlogs,
  getPublishedBlogBySlug,
  saveBlog,
  toBlogDocument,
  toBlogRecord,
  type BlogDocument,
  type BlogRecord,
} from "@/lib/blogs";
import type { SupabaseSession } from "@/lib/supabaseAuth";

type RepositoryResult<T> = {
  data: T;
  source: "supabase" | "local";
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.replace(/\/$/, "") ?? "";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";
const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

const blogSelect =
  "slug,title,excerpt,category,author_name,published_at,read_time_minutes,seo_title,seo_description,cover_image_url,hero_html,body_html,status,author_user_id";

function getHeaders() {
  return {
    apikey: supabaseAnonKey,
    Authorization: `Bearer ${supabaseAnonKey}`,
    "Content-Type": "application/json",
  };
}

async function requestSupabase<T>(path: string, init?: RequestInit) {
  const response = await fetch(`${supabaseUrl}${path}`, {
    ...init,
    headers: {
      ...getHeaders(),
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    throw new Error(`Supabase request failed with status ${response.status}`);
  }

  return (await response.json()) as T;
}

export function hasSupabaseBlogBackend() {
  return isSupabaseConfigured;
}

export async function listBlogs(): Promise<RepositoryResult<BlogDocument[]>> {
  if (!isSupabaseConfigured) {
    return { data: getPublishedBlogs(), source: "local" };
  }

  try {
    const rows = await requestSupabase<BlogRecord[]>(
      `/rest/v1/blogs?select=${encodeURIComponent(blogSelect)}&status=eq.published&order=published_at.desc`,
    );

    const supabaseBlogs = rows.map(toBlogDocument);
    const merged = new Map<string, BlogDocument>();

    [...getPublishedBlogs(), ...supabaseBlogs].forEach((blog) => {
      if (!merged.has(blog.slug)) {
        merged.set(blog.slug, blog);
      }
    });

    return { data: [...merged.values()], source: "supabase" };
  } catch {
    return { data: getPublishedBlogs(), source: "local" };
  }
}

export async function fetchBlogBySlug(slug: string): Promise<RepositoryResult<BlogDocument | null>> {
  if (!isSupabaseConfigured) {
    return { data: getPublishedBlogBySlug(slug), source: "local" };
  }

  try {
    const rows = await requestSupabase<BlogRecord[]>(
      `/rest/v1/blogs?select=${encodeURIComponent(blogSelect)}&slug=eq.${encodeURIComponent(slug)}&status=eq.published&limit=1`,
    );

    const localPublished = getPublishedBlogBySlug(slug);
    return {
      data: rows[0] ? toBlogDocument(rows[0]) : localPublished,
      source: "supabase",
    };
  } catch {
    return { data: getPublishedBlogBySlug(slug), source: "local" };
  }
}

export async function createBlog(
  blog: BlogDocument,
  options?: {
    status?: "draft" | "published" | "archived";
    authorUserId?: string;
    session?: SupabaseSession | null;
  },
): Promise<RepositoryResult<BlogDocument>> {
  if (!isSupabaseConfigured || !options?.session?.access_token || !options.authorUserId) {
    saveBlog(blog);
    return { data: blog, source: "local" };
  }

  const payload = {
    ...toBlogRecord(blog, options?.status ?? "draft"),
    author_user_id: options.authorUserId,
  };

  try {
    const rows = await requestSupabase<BlogRecord[]>("/rest/v1/blogs", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${options.session.access_token}`,
        Prefer: "return=representation",
      },
      body: JSON.stringify(payload),
    });

    const created = rows[0] ? toBlogDocument(rows[0]) : blog;
    return { data: created, source: "supabase" };
  } catch {
    saveBlog(blog);
    return { data: blog, source: "local" };
  }
}

export async function listOwnBlogs(
  session: SupabaseSession | null,
): Promise<RepositoryResult<BlogDocument[]>> {
  if (!isSupabaseConfigured || !session?.access_token || !session.user.id) {
    return { data: getAllBlogs(), source: "local" };
  }

  try {
    const rows = await requestSupabase<BlogRecord[]>(
      `/rest/v1/blogs?select=${encodeURIComponent(blogSelect)}&author_user_id=eq.${encodeURIComponent(session.user.id)}&order=updated_at.desc`,
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      },
    );

    return { data: rows.map(toBlogDocument), source: "supabase" };
  } catch {
    return { data: getAllBlogs(), source: "local" };
  }
}

export async function fetchOwnBlogBySlug(
  slug: string,
  session: SupabaseSession | null,
): Promise<RepositoryResult<BlogDocument | null>> {
  if (!isSupabaseConfigured || !session?.access_token || !session.user.id) {
    return { data: getBlogBySlug(slug), source: "local" };
  }

  try {
    const rows = await requestSupabase<BlogRecord[]>(
      `/rest/v1/blogs?select=${encodeURIComponent(blogSelect)}&slug=eq.${encodeURIComponent(slug)}&author_user_id=eq.${encodeURIComponent(session.user.id)}&limit=1`,
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      },
    );
    return { data: rows[0] ? toBlogDocument(rows[0]) : null, source: "supabase" };
  } catch {
    return { data: getBlogBySlug(slug), source: "local" };
  }
}

export async function updateBlog(
  blog: BlogDocument,
  options: {
    status?: "draft" | "published" | "archived";
    authorUserId: string;
    session: SupabaseSession;
  },
): Promise<RepositoryResult<BlogDocument>> {
  if (!isSupabaseConfigured || !options.session.access_token || !options.authorUserId) {
    saveBlog(blog);
    return { data: { ...blog, status: options.status ?? blog.status }, source: "local" };
  }

  const payload = {
    ...toBlogRecord({ ...blog, status: options.status ?? blog.status }, options.status ?? "draft"),
    author_user_id: options.authorUserId,
  };

  try {
    const rows = await requestSupabase<BlogRecord[]>(
      `/rest/v1/blogs?slug=eq.${encodeURIComponent(blog.slug)}&author_user_id=eq.${encodeURIComponent(options.authorUserId)}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${options.session.access_token}`,
          Prefer: "return=representation",
        },
        body: JSON.stringify(payload),
      },
    );
    const updated = rows[0] ? toBlogDocument(rows[0]) : { ...blog, status: options.status ?? blog.status };
    return { data: updated, source: "supabase" };
  } catch {
    saveBlog({ ...blog, status: options.status ?? blog.status });
    return { data: { ...blog, status: options.status ?? blog.status }, source: "local" };
  }
}

export async function deleteBlog(
  slug: string,
  options?: {
    authorUserId?: string;
    session?: SupabaseSession | null;
  },
): Promise<RepositoryResult<boolean>> {
  if (!isSupabaseConfigured || !options?.session?.access_token || !options.authorUserId) {
    deleteStoredBlog(slug);
    return { data: true, source: "local" };
  }

  try {
    await requestSupabase<null>(
      `/rest/v1/blogs?slug=eq.${encodeURIComponent(slug)}&author_user_id=eq.${encodeURIComponent(options.authorUserId)}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${options.session.access_token}`,
          Prefer: "return=minimal",
        },
      },
    );
    return { data: true, source: "supabase" };
  } catch {
    deleteStoredBlog(slug);
    return { data: true, source: "local" };
  }
}
